import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useDataProvider, useRefresh } from 'react-admin';

interface Branch {
  id: string;
  name: string;
  address?: string;
}

interface BranchContextType {
  branches: Branch[];
  selectedBranchId: string | null;
  selectedBranch: Branch | null;
  setSelectedBranchId: (id: string | null) => void;
  isLoading: boolean;
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

export const BranchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const prevBranchIdRef = useRef<string | null | undefined>(undefined);

  // When the branch changes (after initial load), invalidate the current view so
  // resources re-fetch with the new ?branchId — reflects branch-aware stock.
  useEffect(() => {
    if (isLoading) return;
    if (prevBranchIdRef.current === undefined) {
      prevBranchIdRef.current = selectedBranchId;
      return;
    }
    if (prevBranchIdRef.current !== selectedBranchId) {
      prevBranchIdRef.current = selectedBranchId;
      refresh();
    }
  }, [selectedBranchId, isLoading, refresh]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const { data } = await dataProvider.getList('branches', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        });
        setBranches(data);

        // Load saved branch from localStorage or select "all"
        const savedBranchId = localStorage.getItem('selectedBranchId');
        if (savedBranchId === 'all') {
          setSelectedBranchId(null);
        } else if (savedBranchId && data.some((b: Branch) => b.id === savedBranchId)) {
          setSelectedBranchId(savedBranchId);
        } else {
          // Default to "all branches"
          setSelectedBranchId(null);
        }
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBranches();
  }, [dataProvider]);

  const handleSetSelectedBranchId = (id: string | null) => {
    setSelectedBranchId(id);
    localStorage.setItem('selectedBranchId', id ?? 'all');
  };

  const selectedBranch = branches.find((b) => b.id === selectedBranchId) || null;

  return (
    <BranchContext.Provider
      value={{
        branches,
        selectedBranchId,
        selectedBranch,
        setSelectedBranchId: handleSetSelectedBranchId,
        isLoading,
      }}
    >
      {children}
    </BranchContext.Provider>
  );
};

export const useBranch = (): BranchContextType => {
  const context = useContext(BranchContext);
  if (context === undefined) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};
